import React from 'react';

// 六角形のレーダーチャート。学生アプリの店舗一覧(4.storeList.dart の _HexRadarPainter)と同じ描き方。
// 1〜5点の位置に灰色の六角形の目盛り、中心から6本の軸、値の面(ピンク)と項目名を重ねる。
const MAX_SCORE = 5;
const W = 320;
const H = 278;
const SIDE_LABEL_WIDTH = 62;
const ACCENT = '#E44E70';

type RadarProps = {
  labels: string[];
  values: number[]; // 1〜5(未入力は 0)
};

// 左右の項目名が5文字より長いときだけ、半分あたりで2行に分ける(「クリエイティブ」→「クリエイ／ティブ」)
const sideLines = (label: string): string[] => {
  if (label.length <= 5) return [label];
  const cut = Math.ceil(label.length / 2);
  return [label.slice(0, cut), label.slice(cut)];
};

const Radar: React.FC<RadarProps> = ({ labels, values }) => {
  const cx = W / 2;
  const cy = H / 2;
  const radius = Math.min(W / 2 - SIDE_LABEL_WIDTH - 14, H / 2 - 26);
  const n = 6;
  const point = (i: number, r: number): [number, number] => {
    const a = -Math.PI / 2 + (2 * Math.PI * i) / n; // 真上から時計回り
    return [cx + Math.cos(a) * r, cy + Math.sin(a) * r];
  };
  const poly = (r: (i: number) => number) =>
    Array.from({ length: n }, (_, i) => point(i, r(i)).join(',')).join(' ');
  const ratio = (i: number) => Math.max(0, Math.min(MAX_SCORE, Number(values[i]) || 0)) / MAX_SCORE;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', width: '100%', maxWidth: 320, margin: '0 auto', fontFamily: 'inherit' }} role="img" aria-label="レーダーチャート">
      {Array.from({ length: MAX_SCORE }, (_, k) => (
        <polygon key={k} points={poly(() => (radius * (k + 1)) / MAX_SCORE)} fill="none" stroke="#E2E2E2" strokeWidth={0.7} />
      ))}
      {Array.from({ length: n }, (_, i) => {
        const [x, y] = point(i, radius);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="#E2E2E2" strokeWidth={0.7} />;
      })}
      <circle cx={cx} cy={cy} r={2} fill="#E2E2E2" />

      <polygon points={poly((i) => radius * ratio(i))} fill={ACCENT} fillOpacity={0.18} stroke={ACCENT} strokeWidth={2} strokeLinejoin="round" />
      {Array.from({ length: n }, (_, i) => {
        const [x, y] = point(i, radius * ratio(i));
        return <circle key={i} cx={x} cy={y} r={3} fill={ACCENT} />;
      })}

      {labels.slice(0, n).map((label, i) => {
        const top = i === 0;
        const bottom = i === 3;
        const [px, py] = point(i, radius + 10);
        const lines = top || bottom ? [label] : sideLines(label);
        const anchor = top || bottom ? 'middle' : i < 3 ? 'start' : 'end';
        const lineH = 14;
        // 上は文字の下端を点に、下は上端を点に、左右は縦の中央をそろえる
        const firstY = top ? py - 4 - (lines.length - 1) * lineH
          : bottom ? py + 11
          : py + 4 - ((lines.length - 1) * lineH) / 2;
        return (
          <text key={i} x={px} textAnchor={anchor} fontSize={11.5} fontWeight={800} fill="#1B1B1B">
            {lines.map((line, k) => (
              <tspan key={k} x={px} y={firstY + k * lineH}>{line}</tspan>
            ))}
          </text>
        );
      })}
    </svg>
  );
};

export default Radar;
